import { useEffect, useState } from 'react';
import { act } from '../lib/actions';
import { apiBlob } from '../lib/api';
import type { FileView } from '../lib/types';
import { Icon } from './ui';

const IMAGE = /^image\/(png|jpeg|gif|webp)$/;

export const fileSize = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Saves a file to the computer, fetched with the session. */
export async function saveFile(f: FileView): Promise<void> {
  const blob = await act(() => apiBlob(`/attachments/${f.id}`));
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = f.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** An image shown small (click to save); fetched with the session. */
function Thumb({ f }: { f: FileView }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    apiBlob(`/attachments/${f.id}`)
      .then((b) => setSrc((url = URL.createObjectURL(b))))
      .catch(() => setSrc(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [f.id]);
  return (
    <button type="button" onClick={() => void saveFile(f)} title={`Save ${f.filename}`} className="block overflow-hidden rounded-lg border border-line bg-hull">
      {src ? <img src={src} alt={f.filename} className="max-h-48 max-w-[240px] object-contain" /> : <span className="block px-3 py-6 text-xs text-ink-3">Loading…</span>}
    </button>
  );
}

/** The files on a message: images shown small, others as chips to save. */
export function FileList({ files, light = false }: { files: FileView[]; light?: boolean }) {
  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5" aria-label="Files">
      {files.map((f) => (
        <li key={f.id}>
          {IMAGE.test(f.contentType) ? (
            <Thumb f={f} />
          ) : (
            <button
              type="button"
              onClick={() => void saveFile(f)}
              className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px] ${light ? 'border-white/40 text-on-accent' : 'border-line hover:border-line-strong'}`}
              title={`Save ${f.filename}`}
            >
              <Icon name="clip" size={13} />
              <span className="max-w-[200px] truncate">{f.filename}</span>
              <span className={light ? 'opacity-75' : 'text-ink-3'}>{fileSize(f.size)}</span>
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
